/**
 * WhatsApp-style chat thread for Paul ↔ customer messages and OS outbound (quotes).
 */

import React, { useCallback, useMemo, useState } from "react";
import { Button, Flex, Tooltip, Typography, message as antMessage } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { copyToClipboard } from "../services/communicationTemplateService.js";

const { Text } = Typography;

function formatMessageTime(value) {
  if (!value) return null;
  try {
    return format(new Date(value), "dd MMM · HH:mm");
  } catch {
    return null;
  }
}

export function isOsOutboundMessage(message) {
  if (!message || typeof message !== "object") return false;
  const type = String(message.type ?? message.messageType ?? "").toLowerCase();
  if (type === "os_quote_delivery") return true;
  const role = String(message.role ?? message.speaker ?? "").toLowerCase();
  return role === "bubakii" || role === "os" || role.includes("bubakii");
}

export function isCustomerRole(role) {
  if (!role) return true;
  const normalized = String(role).toLowerCase();
  return (
    normalized.includes("customer") ||
    normalized.includes("user") ||
    normalized.includes("client") ||
    normalized === "inbound"
  );
}

export function resolveMessageVariant(message) {
  if (isOsOutboundMessage(message)) return "os";
  if (isCustomerRole(message?.role)) return "customer";
  return "paul";
}

export function formatRoleLabel(messageOrRole) {
  const message =
    messageOrRole && typeof messageOrRole === "object"
      ? messageOrRole
      : { role: messageOrRole };
  const variant = resolveMessageVariant(message);
  if (variant === "os") return "Bubakii";
  if (variant === "customer") return "Customer";
  return "Paul";
}

function parseTranscriptLine(line, index) {
  const trimmed = String(line || "").trim();
  if (!trimmed) return null;

  const labeled =
    trimmed.match(/^(Customer|Client|User)\s*:\s*(.+)$/i) ||
    trimmed.match(/^(Bubakii|OS)\s*:\s*(.+)$/i) ||
    trimmed.match(/^(Paul|Agent|Bot|Assistant|Agent reply)\s*:\s*(.+)$/i);

  if (labeled) {
    const speaker = labeled[1];
    return {
      index,
      role: speaker,
      text: labeled[2].trim(),
      timestamp: null,
    };
  }

  const bracketed = trimmed.match(
    /^\[[^\]]+\]\s*\[(Customer|Client|User|Paul|Bubakii|OS)\]\s*([\s\S]*)$/i
  );
  if (bracketed) {
    return {
      index,
      role: bracketed[1],
      text: bracketed[2].trim(),
      timestamp: null,
    };
  }

  return {
    index,
    role: index % 2 === 0 ? "customer" : "agent",
    text: trimmed,
    timestamp: null,
  };
}

export function normalizeChatMessages(fullChat) {
  if (!fullChat) return [];

  const structured = Array.isArray(fullChat.messages)
    ? fullChat.messages
        .map((message, index) => {
          const text =
            message?.text ??
            message?.body ??
            message?.content ??
            message?.message ??
            null;
          if (!text || !String(text).trim()) return null;

          const messageType = message.type ?? message.messageType ?? null;
          const role =
            message.role ??
            message.speaker ??
            message.sender ??
            message.direction ??
            null;

          return {
            index: Number.isFinite(Number(message.index))
              ? Number(message.index)
              : index,
            role,
            type: messageType,
            messageType,
            text: String(text).trim(),
            timestamp:
              message.timestamp ??
              message.createdAt ??
              message.sentAt ??
              null,
          };
        })
        .filter(Boolean)
    : [];

  if (structured.length > 0) return structured;

  const transcript = fullChat.transcript?.trim();
  if (!transcript) return [];

  return transcript
    .split("\n")
    .map(parseTranscriptLine)
    .filter(Boolean);
}

const bubbleStyles = {
  customer: {
    align: "flex-start",
    background: "#f4f6f8",
    border: "1px solid #e6ebf0",
    color: "#1f2937",
    radius: "14px 14px 14px 4px",
  },
  paul: {
    align: "flex-end",
    background: "#e8f5ef",
    border: "1px solid #cfe8dc",
    color: "#0f3d31",
    radius: "14px 14px 4px 14px",
  },
  os: {
    align: "flex-end",
    background: "#eef4ff",
    border: "1px solid #c7d7fe",
    color: "#1e3a8a",
    radius: "14px 14px 4px 14px",
  },
};

const selectableTextStyle = {
  margin: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  lineHeight: 1.55,
  color: "#111827",
  userSelect: "text",
  WebkitUserSelect: "text",
  WebkitTouchCallout: "default",
  cursor: "text",
};

export function buildMessageCopyText(message) {
  const label = formatRoleLabel(message);
  const timeLabel = formatMessageTime(message.timestamp);
  const header = timeLabel ? `${label} · ${timeLabel}` : label;
  return `${header}\n${message.text}`;
}

function ChatBubble({ message: chatMessage, showCopyButton = true }) {
  const variant = resolveMessageVariant(chatMessage);
  const palette = bubbleStyles[variant];
  const timeLabel = formatMessageTime(chatMessage.timestamp);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(buildMessageCopyText(chatMessage));
    if (success) {
      setCopied(true);
      antMessage.success("Message copied");
      window.setTimeout(() => setCopied(false), 2000);
      return;
    }
    antMessage.error("Failed to copy message");
  }, [chatMessage]);

  return (
    <Flex justify={palette.align} style={{ width: "100%" }}>
      <Flex
        vertical
        gap={4}
        style={{
          maxWidth: "min(88%, 520px)",
          padding: "10px 12px",
          borderRadius: palette.radius,
          background: palette.background,
          border: palette.border,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          userSelect: "text",
          WebkitUserSelect: "text",
        }}
      >
        <Flex justify="space-between" align="center" gap={8} wrap="wrap">
          <Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.2,
              textTransform: "uppercase",
              color: palette.color,
              userSelect: "text",
            }}
          >
            {formatRoleLabel(chatMessage)}
          </Text>
          <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
            {timeLabel && (
              <Text type="secondary" style={{ fontSize: 11, userSelect: "text" }}>
                {timeLabel}
              </Text>
            )}
            {showCopyButton && (
              <Tooltip title={copied ? "Copied" : "Copy message"}>
                <Button
                  type="text"
                  size="small"
                  aria-label="Copy message"
                  icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopy}
                  style={{
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    padding: 0,
                    color: copied ? "#389e0d" : "#8c8c8c",
                  }}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
        <Text style={selectableTextStyle}>{chatMessage.text}</Text>
      </Flex>
    </Flex>
  );
}

const ConversationChatThread = ({
  fullChat,
  text,
  maxHeight = 420,
  compact = false,
}) => {
  const messages = useMemo(() => {
    const fromFullChat = normalizeChatMessages(fullChat);
    if (fromFullChat.length > 0) return fromFullChat;

    if (text?.trim()) {
      return text
        .split("\n")
        .map(parseTranscriptLine)
        .filter(Boolean);
    }

    return [];
  }, [fullChat, text]);

  if (messages.length === 0) {
    return null;
  }

  const boundedHeight = maxHeight != null && maxHeight !== "";

  return (
    <div
      style={{
        width: "100%",
        ...(boundedHeight ? { maxHeight, overflowY: "auto", overflowX: "hidden" } : {}),
        padding: compact ? "4px 2px" : "8px 4px",
        WebkitOverflowScrolling: "touch",
        userSelect: "text",
        WebkitUserSelect: "text",
      }}
    >
      <Flex vertical gap={compact ? 8 : 10}>
        {messages.map((message) => (
          <ChatBubble
            key={`${message.index}-${message.timestamp || message.text.slice(0, 24)}`}
            message={message}
          />
        ))}
      </Flex>
    </div>
  );
};

export default ConversationChatThread;
